export type ObjVector = readonly [x: number, y: number, z: number];

export type ObjGlobeObject = {
  name: string;
  positions: number[];
  indices: number[];
  lines: ObjVector[][];
};

export type ObjGlobeModel = {
  materialLibrary?: string;
  objects: ObjGlobeObject[];
};

type PendingObject = {
  name: string;
  faces: number[][];
  lines: number[][];
};

export function parseObjGlobeModel(source: string): ObjGlobeModel {
  const vertices: ObjVector[] = [];
  const pending: PendingObject[] = [];
  let materialLibrary: string | undefined;
  let current = createPendingObject("Default");
  pending.push(current);

  for (const rawLine of source.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line || line.startsWith("#")) continue;
    const [directive, ...values] = line.split(/\s+/);
    if (directive === "mtllib") materialLibrary = values.join(" ");
    if (directive === "o") {
      current = createPendingObject(values.join("_") || `Object_${pending.length}`);
      pending.push(current);
    } else if (directive === "v" && values.length >= 3) {
      const vector = values.slice(0, 3).map(Number) as [number, number, number];
      if (vector.every(Number.isFinite)) vertices.push(vector);
    } else if (directive === "f") {
      const face = values.map((value) => resolveObjIndex(value, vertices.length)).filter((value) => value >= 0);
      if (face.length >= 3) current.faces.push(face);
    } else if (directive === "l") {
      const points = values.map((value) => resolveObjIndex(value, vertices.length)).filter((value) => value >= 0);
      if (points.length >= 2) current.lines.push(points);
    }
  }

  return {
    materialLibrary,
    objects: pending.filter((object) => object.faces.length || object.lines.length).map((object) => localizeObject(object, vertices)),
  };
}

export function findObjObject(model: ObjGlobeModel, name: string) {
  return model.objects.find((object) => object.name === name);
}

export function getObjBoundaryLines(model: ObjGlobeModel) {
  return model.objects.filter((object) => object.name.startsWith("Boundary_")).flatMap((object) => object.lines);
}

function localizeObject(object: PendingObject, vertices: ObjVector[]): ObjGlobeObject {
  const localIndices = new Map<number, number>();
  const positions: number[] = [];
  const getVertex = (globalIndex: number) => {
    const vertex = vertices[globalIndex];
    if (!vertex) throw new Error(`OBJ vertex index ${globalIndex + 1} is out of range`);
    return vertex;
  };
  const localIndex = (globalIndex: number) => {
    const existing = localIndices.get(globalIndex);
    if (existing !== undefined) return existing;
    const vertex = getVertex(globalIndex);
    const index = positions.length / 3;
    positions.push(...vertex);
    localIndices.set(globalIndex, index);
    return index;
  };
  const indices: number[] = [];
  for (const face of object.faces) {
    for (let index = 1; index < face.length - 1; index += 1) {
      const first = face[0];
      const second = face[index];
      const third = face[index + 1];
      if (first === undefined || second === undefined || third === undefined) continue;
      indices.push(localIndex(first), localIndex(second), localIndex(third));
    }
  }
  return {
    name: object.name,
    positions,
    indices,
    lines: object.lines.map((line) => line.map(getVertex)),
  };
}

function resolveObjIndex(value: string, vertexCount: number) {
  const raw = Number(value.split("/")[0]);
  if (!Number.isInteger(raw) || raw === 0) return -1;
  return raw > 0 ? raw - 1 : vertexCount + raw;
}

function createPendingObject(name: string): PendingObject {
  return { name, faces: [], lines: [] };
}
